<template>
  <v-card color="white" light v-if="Object.keys(userProfile).length > 0" height="500px">
      <template v-if="stage === STAGES.STAGE_EDIT">
        <v-card-title class="pl-15 pr-15 headline mb-0">{{ selectedReminder.subject }}</v-card-title>
        <v-card-text class="pl-15 pr-15 scrollable">
          <v-alert dismissible type="error" v-model="requiredError">Please provide your first name, last name, title, email address and at least one phone number.</v-alert>
          <div v-html="selectedReminder.body"></div>
          <v-row v-for="(item) in fields" v-bind:key="item.field">
            <v-col cols="3">{{ item.label}}</v-col>
            <v-col cols="9">
              <inline-edit
                :ref="item.field"
                :label="item.label"
                :val="userProfile[item.field]"
                :component-type="item.componentType"
                :persist="(val) => {requiredError = false; userProfile[item.field] = val}"
                :rules="getRules(item.field)"
                class="edit-component"
              ></inline-edit>
            </v-col>
          </v-row>
          <p class="mt-4"><strong>Note: We do not allow sharing of accounts. Everyone in your organization is required to have their own login credentials. Please contact <a href="mailto:trading@upstatecoins.com" target="_blank">trading@upstatecoins.com</a> to add additional users to your account.</strong></p>
        </v-card-text>
        <v-card-actions class="pl-15 pr-15" light>
          <v-btn
            absolute
            bottom
            right
            :loading="loading"
            @click="save"
            color="primary"
          >Confirm
          </v-btn>
        </v-card-actions>
      </template>
      <template v-if="stage === STAGES.STAGE_VERIFY">
        <v-card-title class="pl-15 pr-15 headline mb-0">{{ selectedReminder.subject }}</v-card-title>
        <v-card-text class="pl-15 pr-15" >
          <p>A verification code has been sent to your new email address <strong>{{ userProfile.email }}</strong>.</p>
          <p>Please enter it below to complete your update:</p>
          <v-row>
            <v-col cols="12">
              <v-text-field
                label="6-Digit Verification Code"
                @keypress="verificationError = false"
                counter
                maxlength="6"
                v-model="verificationCode">
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pl-15 pr-15" light>
          <v-btn
            absolute
            bottom
            left
            @click="stage = STAGES.STAGE_EDIT"
          >Back
          </v-btn>
          <v-btn
            absolute
            bottom
            right
            @click="saveVerification"
            :loading="loading"
            color="primary"
          >Confirm
          </v-btn>
        </v-card-actions>
      </template>
      <template v-if="stage === STAGES.STAGE_COMPLETE">
        <v-card-title class="pl-15 pr-15 headline mb-0">{{ selectedReminder.subject }}</v-card-title>
        <v-card-text class="pl-15 pr-15">
          <v-alert type="success">Thank you for confirming your contact information. You may now close this window.</v-alert>
        </v-card-text>
        <v-card-actions class="pl-15 pr-15" light>
          <v-btn
            absolute
            bottom
            right
            :loading="loading"
            color="primary"
            @click="dismiss"
          >Close
          </v-btn>
        </v-card-actions>
      </template>
  </v-card>
</template>

<script>
import {mapGetters} from "vuex";
import InlineEdit from '@/components/InlineEdit'
import {pick} from 'lodash'
import validationMixin from '@/mixins/validation'

const STAGES = {
  STAGE_EDIT: `edit`,
  STAGE_VERIFY: `verify`,
  STAGE_COMPLETE: `complete`
}

export default {
  components: {
    InlineEdit
  },
  props: {
    selectedReminder: {
      type: Object,
      default: () => {
        return {
          subject: ``,
          body: ``
        }
      }
    }
  },
  watch: {
    verificationCode: {
      handler(val) {
        if (val && val.length === 6) {
          this.saveVerification()
        }
      }
    }
  },
  mixins: [validationMixin],
  computed: {
    ...mapGetters([
      'getUserProfile',
    ])
  },
  data() {
    return {
      STAGES,
      stage: STAGES.STAGE_EDIT,
      pick,
      loading: false,
      verificationCode: null,
      requiredError: false,
      fields: [
        { field: `firstName`, label: `First Name`, componentType: `textfield` },
        { field: `lastName`, label: `Last Name`, componentType: `textfield` },
        { field: `title`, label: `Title`, componentType: `textfield` },
        { field: `phoneNumber`, label: `Office Phone`, componentType: `phone` },
        { field: `cellnumber`, label: `Mobile Number`, componentType: `phone` },
        { field: `email`, label: `Email`, componentType: `textfield` },
      ],
      userProfile: {},
    }
  },
  methods: {
    reducedUserProfile(userProfile) {
      return pick(userProfile, this.fields.map(field => field.field))
    },
    dismiss() {
      this.$emit('dismiss')
    },
    async saveVerification() {
      this.loading = true
      try {
        await this.$store.dispatch('updateUserProfile', {
          ...this.userProfile,
          emailChangeVerificationCode: this.verificationCode
        })
        this.stage = STAGES.STAGE_COMPLETE
      } catch (err) {
        window._VMA.snackbar = {
          show: true,
          text: err.response.data,
          color: 'red',
        }
      }
      this.loading = false
    },
    validate() {
      const required = [`firstName`, `lastName`, `title`, `email`]
      for (let require of required) {
        if (!this.userProfile[require]) {
          this.$refs[require][0].enterEditMode()
          this.requiredError = true
          return false
        }
      }
      if (!this.userProfile.phoneNumber.trim() && !this.userProfile.cellnumber.trim()) {
        this.$refs.phoneNumber[0].enterEditMode()
        this.requiredError = true
        return false
      }
      return true
    },
    async save() {
      if (this.validate()) {
        this.loading = true
        try {
          if (this.userProfile.email !== this.getUserProfile.email) {
            await this.$store.dispatch('changeEmailRequest', {newEmailAddress: this.userProfile.email})
            this.stage = STAGES.STAGE_VERIFY
          } else if (JSON.stringify(this.userProfile) !== JSON.stringify(this.reducedUserProfile(this.getUserProfile))) {
            await this.$store.dispatch('updateUserProfile', this.userProfile)
            this.stage = STAGES.STAGE_COMPLETE
          } else {
            this.stage = STAGES.STAGE_COMPLETE
          }
        } catch (err) {
          window._VMA.snackbar = {
            show: true,
            text: err.response.data,
            color: 'red',
          }
        }
        this.loading = false
      }
    },
    getRules(field) {
      switch (field) {
        case `email`:
          return this.emailRules.concat(this.stringRequired)
        default:
          return []
      }
    }
  },
  mounted() {
    this.userProfile = this.reducedUserProfile(this.getUserProfile)
  }
}
</script>

<style scoped>
.scrollable {
  height: 400px;
  overflow-y: auto;
}
</style>